/* eslint-disable prefer-destructuring */
import axios from 'axios';
import getReportPdfExportStyles from '../../utils/reportPdfExportStyles';
import { message } from 'antd';
import { createBrowserHistory } from 'history';
import * as HttpStatus from 'http-status-codes';
import { z } from 'zod';
// eslint-disable-next-line import/no-cycle
import { logout } from '../Login/LoginAction';
import { fetchOrganisations } from '../../scenes/Organisation/OrganisationAction';
import { fetchPlans } from '../../scenes/Dashboard/scenes/Plan/PlanAction';
import { fetchUser } from '../../scenes/Admin/scenes/User/UserActions';
import { fetchBroker } from '../../scenes/Admin/scenes/Broker/BrokerActions';
import { secureStorage } from '../../utils/secureStorage';
import { validateUrlParams, urlParamSchemas } from '../../utils/urlValidation';
import {
  attachUnstructuredApiErrorDebugHelpers,
  getBusinessUnauthorizedMessage,
  normalizeHttpReject,
  prepareBusinessUnauthorizedReject,
  reportUnstructuredApiError,
  resolveUnauthorizedInterceptorAction,
} from '../../utils/httpError';
import resolvePostLoginPath from '../Login/resolvePostLoginPath';
import { getExportHtmlBodyAttributes, getReportExportBrandColors } from '../../themes/partnerTheme';
import getPartnerExportFontFaceHtml from '../../utils/partnerExportFonts';
import absolutizeExportHtmlAssets from '../../utils/absolutizeExportHtmlAssets';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_AUTH_TOKEN = 'auth/token';
const URL_TOKEN_EXCLUDED_PATHS = [
  '/confirmation/email',
  '/reset-password',
  '/authenticator-recovery',
  '/update-email',
];

const SESSION_EXPIRED_MSG = 'Your session has expired or is no longer valid. You will be signed out.';

/** setTimeout overflows above 2^31-1 ms (~24.8 days); long sessions re-arm in chunks. */
const MAX_TIMER_DELAY_MS = 2147483647;

/** Avoid duplicate toasts and repeated `logout` when many requests return 401 together. */
let sessionExpiryLogoutInProgress = false;

/** `initialize` runs on every login — register global handlers only once. */
let responseInterceptorAttached = false;
let storageListenerAttached = false;
let sessionExpiryTimer = null;

function pathIndicatesPublicEntry() {
  const p = window.location.pathname;
  return (
    p === '/' ||
    p === '/admin-login' ||
    p.startsWith('/onboarding/') ||
    p.startsWith('/confirmation/') ||
    p.startsWith('/invitation/') ||
    p.startsWith('/reset-password') ||
    p.startsWith('/authenticator-recovery') ||
    p.startsWith('/update-email')
  );
}

/** Routes where `token` in the query string is an invitation token, not a JWT. */
function urlAuthTokenParamExcluded() {
  const p = window.location.pathname;
  return URL_TOKEN_EXCLUDED_PATHS.includes(p) || p.startsWith('/onboarding/') || p.startsWith('/invitation/');
}

/** Unauthenticated flows — do not hydrate org/plan/user session state. */
function shouldSkipSessionBootstrap() {
  const p = window.location.pathname;
  if (p === '/onboarding/paypal/return' || p === '/onboarding/paypal/cancelled') return false;
  return (
    p.startsWith('/onboarding/') ||
    p.startsWith('/invitation/') ||
    p.startsWith('/confirmation/') ||
    p.startsWith('/reset-password') ||
    p.startsWith('/authenticator-recovery') ||
    p.startsWith('/update-email')
  );
}

function authUrlIndicatesCredentialRequest(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return (
    u.includes('auth/token') ||
    u.includes('auth/2fa-verify') ||
    u.includes('auth/credential') ||
    u.includes('auth/clientcredential') ||
    u.includes('auth/logout')
  );
}

/** Full navigation so React Router remounts cleanly after credentials are cleared. */
function redirectToLandingPage() {
  if (window.location.pathname === '/') return;
  window.location.assign('/');
}

/**
 * Session invalidation: show notice, clear credentials, then go to the landing page.
 * @returns {boolean} true when this 401 was treated as an expired/invalid session (do not treat as a normal login error).
 */
function handleSessionUnauthorized(dispatch, error) {
  const cfg = error?.config;
  if (pathIndicatesPublicEntry() || authUrlIndicatesCredentialRequest(cfg?.url)) {
    return false;
  }
  if (sessionExpiryLogoutInProgress) {
    return true;
  }
  sessionExpiryLogoutInProgress = true;
  message.warning(SESSION_EXPIRED_MSG, 4, () => {
    sessionExpiryLogoutInProgress = false;
  });
  dispatch(logout({ revoke: false }));
  redirectToLandingPage();
  return true;
}

function expireSession(dispatch) {
  message.warning(SESSION_EXPIRED_MSG, 4);
  dispatch(logout({ revoke: false }));
  redirectToLandingPage();
}

function getStoredExpiryMs() {
  const expiry = secureStorage.getItem('expiry');
  const expiryMs = expiry != null ? Number(expiry) : NaN;
  return Number.isFinite(expiryMs) ? expiryMs : null;
}

/** Sign out when the stored expiry passes, instead of waiting for the next 401. */
function scheduleSessionExpiry(dispatch) {
  clearTimeout(sessionExpiryTimer);
  sessionExpiryTimer = null;
  const expiryMs = getStoredExpiryMs();
  if (expiryMs == null) return;

  const delay = Math.max(0, expiryMs - Date.now());
  sessionExpiryTimer = setTimeout(() => {
    sessionExpiryTimer = null;
    if (!secureStorage.getItem('token')) return;
    const latestExpiryMs = getStoredExpiryMs();
    if (latestExpiryMs != null && latestExpiryMs > Date.now()) {
      scheduleSessionExpiry(dispatch);
      return;
    }
    expireSession(dispatch);
  }, Math.min(delay, MAX_TIMER_DELAY_MS));
}

/** Another tab signed out (token removed or storage cleared) — sign this tab out too. */
function attachCrossTabLogoutListener(dispatch) {
  if (storageListenerAttached) return;
  storageListenerAttached = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== 'token' && event.key !== null) return;
    if (event.newValue) return;
    if (pathIndicatesPublicEntry()) return;
    dispatch(logout({ revoke: false }));
    redirectToLandingPage();
  });
}

/** `exp` claim (seconds) → ms, so a JWT from the URL does not inherit a stale stored expiry. */
function getJwtExpiryMs(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Tokens in the address bar leak via history, logs and Referer — persist it, then drop it from the URL. */
function adoptQueryToken(token) {
  secureStorage.setItem('token', token);
  const expiryMs = getJwtExpiryMs(token);
  if (expiryMs != null) secureStorage.setItem('expiry', expiryMs);
  else secureStorage.removeItem('expiry');

  const next = new URL(window.location.href);
  next.searchParams.delete('token');
  window.history.replaceState(window.history.state, '', `${next.pathname}${next.search}${next.hash}`);
}

export const types = {
  APP_INITIALIZING: 'app/APP_INITIALIZING',
  APP_INITIALIZED: 'app/APP_INITIALIZED',
};

const isBase64 = (str) => {
  if (!str || !str.trim()) {
    return false;
  }
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

/**
 * Different types of URLs with token:
 *
 * 1. `token`: JWT token. If API key is not presented, use the default one.
 * 2. `token`: Base64 encoded LinkGen parameters acquired from LMI authentication service. API key MUST be presented in query string.
 * 3. `key` and `EncryptedData`: Raw LinkGen parameters. Usually sent from LMI services. If API key is not presented, use the default one.
 *
 */
class AccessRequestUrl {
  constructor() {
    this.url = new URL(window.location);
  }

  get linkgenToken() {
    console.log('--- Getting LinkGen parameters');

    // Handle raw LinkGen parameters
    const key = this.url.searchParams.get('key');
    const encryptedData = this.url.searchParams.get('EncryptedData');
    if (key && encryptedData) {
      const validation = validateUrlParams(
        { key, EncryptedData: encryptedData },
        z.object({
          key: urlParamSchemas.key,
          EncryptedData: urlParamSchemas.EncryptedData,
        }),
      );

      if (!validation) {
        console.warn('Invalid LinkGen parameters detected');
        return undefined;
      }

      return btoa(`key=${key}&EncryptedData=${encodeURIComponent(encryptedData)}`);
    }

    console.log('--- Getting base64 LinkGen parameters');
    // Handle base64 encoded LinkGen parameters
    const token = this.url.searchParams.get('linkgen');
    if (token) {
      const validation = validateUrlParams({ linkgen: token }, z.object({ linkgen: urlParamSchemas.linkgen }));

      if (!validation || !isBase64(token)) {
        console.warn('Invalid linkgen token detected');
        return undefined;
      }

      return token;
    }

    return undefined;
  }

  /** JWT passed as `?token=` (not on invitation / reset routes, where `token` means something else). */
  get queryToken() {
    if (urlAuthTokenParamExcluded()) return undefined;

    const urlToken = this.url.searchParams.get('token');
    if (!urlToken) return undefined;

    const validation = validateUrlParams({ token: urlToken }, z.object({ token: urlParamSchemas.token }));
    if (!validation) {
      console.warn('Invalid token parameter detected');
      return undefined;
    }

    return urlToken;
  }
}

export const initialize =
  (options = {}) =>
  async (dispatch) => {
    sessionExpiryLogoutInProgress = false;
    dispatch({ type: types.APP_INITIALIZING });

    attachUnstructuredApiErrorDebugHelpers();
    attachCrossTabLogoutListener(dispatch);

    if (!responseInterceptorAttached) {
      responseInterceptorAttached = true;
      axios.interceptors.response.use(
        (res) => res,
        (error) => {
          const response = error?.response;
          const status = response?.status;
          const rejected = normalizeHttpReject(error);
          // Collect empty/unstructured error bodies for BE follow-up (dev / opt-in).
          reportUnstructuredApiError(error);

          switch (status) {
            // Business auth (e.g. unauthorized_access, access_denied): show errorMessage, stay logged in.
            // Session invalid / no code: logout via handleSessionUnauthorized (skipped on login/public routes).
            case HttpStatus.UNAUTHORIZED: {
              const action = resolveUnauthorizedInterceptorAction(error);
              if (action === 'business') {
                const businessRejected = prepareBusinessUnauthorizedReject(error);
                message.error(getBusinessUnauthorizedMessage(businessRejected));
                return Promise.reject(businessRejected);
              }
              handleSessionUnauthorized(dispatch, error);
              return Promise.reject(rejected);
            }
            // Components show MSG_PAYMENT_REQUIRED — do not toast here.
            case HttpStatus.PAYMENT_REQUIRED:
              return Promise.reject(rejected);
            // Import validation — keep normalized reject for err.status / err.data in catch blocks.
            case HttpStatus.EXPECTATION_FAILED:
              return Promise.reject(rejected);
            default:
              return Promise.reject(rejected);
          }
        },
      );
    }

    const url = new AccessRequestUrl();

    // A JWT in the query string replaces any stored session (and its stale expiry) before the expiry check.
    const queryToken = url.queryToken;
    if (queryToken) adoptQueryToken(queryToken);

    // first check if the current token is expired
    const expiryMs = getStoredExpiryMs();
    if (expiryMs != null && expiryMs < Date.now()) {
      expireSession(dispatch);
    } else {
      // Check for LinkGen access
      let token = url.linkgenToken;
      if (token) {
        console.log('--- Found LinkGen token in URL');
        // axios.defaults.headers.common.Authorization = `LinkGen ${token}`;
        try {
          console.log('--- Exchange for JWT token');
          const res = await axios.get(`${API_BASE}${API_AUTH_TOKEN}`, {
            headers: {
              Authorization: `LinkGen ${token}`,
            },
          });
          token = res.data.access_token;
          secureStorage.setItem('token', token);
          secureStorage.setItem('expiry', res.data.expiry);
        } catch (err) {
          console.error('--- Failed to exchange for JWT token', err);
          return;
        }
      } else {
        console.log('--- Getting JWT token from local storage');
        token = secureStorage.getItem('token');
        // No need to get JWT token again
      }

      const skipSessionBootstrap = !options.forceBootstrap && shouldSkipSessionBootstrap();

      if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        scheduleSessionExpiry(dispatch);

        if (!skipSessionBootstrap) {
          let brokerResponse = null;
          let organisationList = null;
          const promises = [
            dispatch(fetchOrganisations()).then((response) => {
              organisationList = response?.data;
              // Plans must load after org context — unscoped GET /plans returns all orgs' plans.
              return dispatch(fetchPlans());
            }),
            dispatch(fetchUser()),
            dispatch(fetchBroker())
              .then((response) => {
                brokerResponse = response;
                return response;
              })
              .catch(() => null),
          ];
          try {
            await Promise.all(promises);
            const path = window.location.pathname;
            // After login, leave public auth pages (reset-password, invitation, etc.) as well as `/` / onboarding.
            // forceBootstrap is required for onboarding so we do not steal mid-wizard sessions.
            const isPublicAuthCompletionPath =
              path.startsWith('/reset-password') ||
              path.startsWith('/invitation/') ||
              path.startsWith('/authenticator-recovery');
            const shouldResolveLanding =
              path === '/' ||
              (options.forceBootstrap && (path.startsWith('/onboarding/') || isPublicAuthCompletionPath));
            if (shouldResolveLanding) {
              const redirectPath = await resolvePostLoginPath({
                fetchBroker: async () => brokerResponse,
                organisationList,
              });
              // Hard navigate off public auth URLs so the reset/invitation form cannot remain mounted.
              if (isPublicAuthCompletionPath) window.location.assign(redirectPath);
              else {
                const history = createBrowserHistory();
                history.push(redirectPath);
              }
            }
          } catch (err) {
            console.log(err);
          }
        }
      } else {
        delete axios.defaults.headers.common.Authorization;
      }
    }
    dispatch({ type: types.APP_INITIALIZED });
  };

const escapeHtmlAttr = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');

const htmlTemplate = async (htmlBody, { pageMargin } = {}) => {
  if (!htmlBody) return null;

  const { className, style } = getExportHtmlBodyAttributes();
  const { navy, primary } = getReportExportBrandColors();
  const bodyClass = className ? ` class="${className}"` : '';
  const bodyStyle = style ? ` style="${escapeHtmlAttr(style)}"` : '';
  const rootVarsStyle = style ? `<style>:root{${style}}</style>` : '';
  const partnerFontFaceHtml = await getPartnerExportFontFaceHtml();
  const reportPdfExportStyles = getReportPdfExportStyles({ pageMargin });
  const absolutizedBody = absolutizeExportHtmlAssets(htmlBody);
  // Hex overrides for Policy, Plan, Risk report, and Certificate PDF exports.
  // Survive when linked stylesheets fail on the export host or CSS vars are missing.
  const brandOverrideStyle = `
    <style>
      body.partner-aviva {
        font-family: var(--partner-font-family, 'Source Sans 3', Roboto, Arial, sans-serif) !important;
      }

      body.partner-aviva h1,
      body.partner-aviva h2,
      body.partner-aviva h3,
      body.partner-aviva h4,
      body.partner-aviva .title,
      body.partner-aviva .policy-preview-cover .title,
      body.partner-aviva .plan-preview-cover .title,
      body.partner-aviva .risk-report-preview-cover .title {
        font-family: var(--partner-font-display-family, 'Aviva Curve', Roboto, Arial, sans-serif) !important;
      }

      .risk-report-preview-cover .title > h1,
      .policy-preview-cover .title > h1,
      .plan-preview-cover .title > h1 { color: ${primary} !important; }

      .risk-report-preview-main .title .shape,
      .risk-report-preview-main .table-top-action-row,
      .risk-report-preview-disclaimer .disclaimer-content,
      .plan-preview-page .title .shape,
      .plan-preview-page .title h1.shape,
      .plan-preview-page > footer .page-number,
      .plan-preview-disclaimer .disclaimer-content,
      .policy-preview-page .title .shape,
      .policy-preview-page .title h1.shape,
      .policy-preview-page > footer .page-number,
      .policy-preview-disclaimer .disclaimer-content {
        background-color: ${navy} !important;
        color: #fff !important;
      }

      .risk-report-preview-main h1,
      .risk-report-preview-main h2,
      .risk-report-preview-main h3,
      .risk-report-preview-main .table-top-row,
      .risk-report-preview-main .table-content span,
      .plan-preview-page h1:not(.shape),
      .plan-preview-page h2,
      .plan-preview-page h3,
      .plan-preview-navigation .instruction .title,
      .policy-preview-page h1:not(.shape),
      .policy-preview-page h2,
      .policy-preview-page h3,
      .certificate-inner h1,
      .certificate-inner h3 {
        color: ${navy} !important;
      }

      .plan-preview-page > header .plan-document,
      .policy-preview-page > header .policy-document,
      .certificate-inner h2 {
        color: ${primary} !important;
      }

      .plan-preview-page > header .header-bar,
      .policy-preview-page > header .header-bar {
        background-image: none !important;
        background: linear-gradient(90deg, ${navy} 0%, ${primary} 100%) !important;
      }

      .plan-preview-table .table-top-action-row,
      .plan-preview-table .table-header,
      .plan-preview-table .table-header .table-top-row,
      .plan-preview-table .table-header .table-top-row strong,
      .plan-preview-table .table-top-action-row strong,
      .policy-preview-page .table-top-action-row,
      .policy-preview-page .table-header,
      .policy-preview-page .table-header .table-top-row,
      .policy-preview-page .table-header .table-top-row strong,
      .policy-preview-page .table-top-action-row strong {
        background-color: ${navy} !important;
        color: #fff !important;
        border-color: ${navy} !important;
      }

      .risk-report-preview-main .table-header,
      .plan-preview-table,
      .plan-preview-table .table-header,
      .policy-preview-page .table-header {
        border-top-color: ${navy} !important;
      }

      .plan-preview-table .table-content,
      .plan-preview-table .table-content span,
      .policy-preview-page .table-content,
      .policy-preview-page .table-content span {
        color: ${navy} !important;
      }

      /* Body column-label rows only — not thead .table-header .table-top-row */
      .plan-preview-table tbody .table-top-row,
      .policy-preview-page tbody .table-top-row {
        color: ${navy} !important;
      }

      .risk-report-preview-disclaimer .disclaimer-content h2,
      .risk-report-preview-disclaimer .disclaimer-content p,
      .risk-report-preview-disclaimer .disclaimer-content li,
      .plan-preview-disclaimer .disclaimer-content h2,
      .plan-preview-disclaimer .disclaimer-content p,
      .plan-preview-disclaimer .disclaimer-content li,
      .policy-preview-disclaimer .disclaimer-content h2,
      .policy-preview-disclaimer .disclaimer-content p,
      .policy-preview-disclaimer .disclaimer-content li {
        color: #fff !important;
      }
    </style>`;

  return `
    <html>
      <head>
        <meta charset="utf-8">
        <base href="${typeof window !== 'undefined' ? window.location.origin : ''}/" />
        ${
          process.env.NODE_ENV === 'development'
            ? [...document.head.querySelectorAll('style')].map((item) => item.outerHTML).join('')
            : [...document.head.querySelectorAll('link[rel="stylesheet"]')]
                .map((item) => `<link href="${item.href}" rel="stylesheet" />`)
                .join('')
        }
        <style>${reportPdfExportStyles}</style>
        ${partnerFontFaceHtml}
        ${rootVarsStyle}
        ${brandOverrideStyle}
        <title></title>
      </head>
      <body${bodyClass}${bodyStyle}>
        ${absolutizedBody}
      </body>
    </html>`;
};

export const download =
  (content, cover, params = {}) =>
  async () => {
    const url = process.env.REACT_APP_API_EXPORT_PDF;
    if (url && !process.env.REACT_APP_APIM_KEY) {
      return Promise.reject(
        new Error(
          'PDF export is missing REACT_APP_APIM_KEY. Add it to .env.local (see .env.dev) and restart the dev server.',
        ),
      );
    }
    const headers = {};
    if (process.env.REACT_APP_APIM_KEY) {
      headers['Ocp-Apim-Subscription-Key'] = process.env.REACT_APP_APIM_KEY;
    }
    // Risk (and any export with Puppeteer header/footer) must not use @page { margin: 0 },
    // or body content paints under the chrome on every page. Covers stay full-bleed.
    const contentPageMargin =
      params.displayHeaderFooter && params.margin
        ? `${params.margin.top || 0} ${params.margin.right || 0} ${params.margin.bottom || 0} ${params.margin.left || 0}`
        : undefined;
    const [contentHtml, frontMatterHtml] = await Promise.all([
      htmlTemplate(content, { pageMargin: contentPageMargin }),
      htmlTemplate(cover, { pageMargin: undefined }),
    ]);
    return axios.post(
      url,
      { content: contentHtml, frontMatter: frontMatterHtml, format: 'A4', ...params },
      { responseType: 'arraybuffer', headers },
    );
  };
